/**
 * The two things worth saying to an Opus encoder, said in the only place they
 * can be said: the session description.
 *
 * Everything else about a call is set through `RTCRtpSender.setParameters`,
 * which is the supported, non-textual way and is what `capBitrate` uses. Opus
 * has parameters that live nowhere else — they are negotiated in the `fmtp`
 * line and there is no API for them — so this is the one place in the app that
 * edits SDP, and it edits exactly two keys.
 *
 * **`useinbandfec=1`** asks the *other* encoder for forward error correction: a
 * low-bitrate copy of the previous packet carried inside the next one, which is
 * how Opus survives loss without waiting for a retransmission. Measured on a
 * call between two iPhones, one of them on a Russian mobile network: a stretch
 * with 13–18 % loss, round trips of half a second and eight seconds of frozen
 * picture. Video has NACK and PLI to lean on and did recover; audio has neither,
 * and a lost packet is simply a hole in what somebody said.
 *
 * **`maxaveragebitrate`** is the audio cap, in the units Opus takes it. The cap
 * set through `setParameters` bounds what the *sender* is allowed to put on the
 * wire, which is not the same as telling the codec what to aim for — and FEC
 * only exists in the room left between the two. Saying it here is what makes
 * the admin page's «битрейт звука» mean something to the encoder rather than
 * only to the sender.
 *
 * Both are written into the *remote* description rather than into our own
 * offer, which is the counter-intuitive half: an encoder is configured by what
 * the far end said it wants. Our own offer already carries what we want, and
 * every implementation this app talks to fills it in the same way.
 *
 * Pure, and tested against the shapes real browsers produce — an fmtp line that
 * is already there, one that is missing entirely, two payload types for one
 * codec, CRLF endings, and an SDP with no Opus in it at all.
 */

export interface OpusOptions {
  /** Ask the far end's encoder for in-band forward error correction. */
  fec: boolean;
  /** What the codec should aim for, in bits per second. Null leaves it alone. */
  maxAverageBitrate: number | null;
}

/** `a=rtpmap:111 opus/48000/2` → 111. Case-insensitive: some stacks shout it. */
function opusPayloadTypes(lines: readonly string[]): string[] {
  const types: string[] = [];
  for (const line of lines) {
    const match = /^a=rtpmap:(\d+)\s+opus\/48000/i.exec(line);
    if (match) types.push(match[1]!);
  }
  return types;
}

/** `minptime=10;useinbandfec=1` → a map, in the order the keys arrived. */
function parseParameters(value: string): Map<string, string> {
  const parameters = new Map<string, string>();
  for (const part of value.split(';')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const equals = trimmed.indexOf('=');
    if (equals === -1) parameters.set(trimmed, '');
    else parameters.set(trimmed.slice(0, equals), trimmed.slice(equals + 1));
  }
  return parameters;
}

function formatParameters(parameters: Map<string, string>): string {
  return [...parameters]
    .map(([key, value]) => (value === '' ? key : `${key}=${value}`))
    .join(';');
}

/**
 * The same SDP with Opus told what it is allowed to do.
 *
 * Untouched when there is no Opus in it — a video-only m-line, or a stack that
 * negotiated something else — and untouched when the options ask for nothing.
 * A description this could not parse comes back exactly as it arrived: a call
 * with default audio is a working call, and a mangled SDP is not.
 */
export function withOpusOptions(sdp: string, options: OpusOptions): string {
  if (!options.fec && options.maxAverageBitrate === null) return sdp;
  // Kept as they came: an SDP mixes CRLF and LF depending on who wrote it, and
  // rewriting the endings of a description is not this function's business.
  const lines = sdp.split('\n');
  const types = opusPayloadTypes(lines);
  if (types.length === 0) return sdp;

  const result: string[] = [];
  const written = new Set<string>();

  const parametersFor = (existing: string): string => {
    const parameters = parseParameters(existing);
    if (options.fec) parameters.set('useinbandfec', '1');
    if (options.maxAverageBitrate !== null) {
      parameters.set('maxaveragebitrate', String(Math.round(options.maxAverageBitrate)));
    }
    return formatParameters(parameters);
  };

  for (const line of lines) {
    const fmtp = /^a=fmtp:(\d+)\s+(.*)$/.exec(line.replace(/\r$/, ''));
    if (fmtp && types.includes(fmtp[1]!)) {
      written.add(fmtp[1]!);
      result.push(`a=fmtp:${fmtp[1]} ${parametersFor(fmtp[2]!)}${line.endsWith('\r') ? '\r' : ''}`);
      continue;
    }
    result.push(line);
  }

  // A payload type with no fmtp line of its own gets one, placed right after
  // its rtpmap so the description reads the way every other one does.
  const missing = types.filter((type) => !written.has(type));
  if (missing.length === 0) return result.join('\n');

  const withFmtp: string[] = [];
  for (const line of result) {
    withFmtp.push(line);
    const rtpmap = /^a=rtpmap:(\d+)\s+opus\/48000/i.exec(line.replace(/\r$/, ''));
    if (rtpmap && missing.includes(rtpmap[1]!)) {
      withFmtp.push(`a=fmtp:${rtpmap[1]} ${parametersFor('')}${line.endsWith('\r') ? '\r' : ''}`);
    }
  }
  return withFmtp.join('\n');
}

/**
 * What Opus actually ended up negotiated as, as one line — or null when there
 * is no Opus in this description.
 *
 * Written into the call's log once per call, because the interesting question
 * about FEC is not «did we ask» but «what did the two ends agree on», and no
 * statistic reports it. `useinbandfec=1` appearing here without anybody having
 * set it is the answer to whether the setting was needed at all.
 */
export function describeOpus(sdp: string): string | null {
  const lines = sdp.split('\n').map((line) => line.replace(/\r$/, ''));
  const [type] = opusPayloadTypes(lines);
  if (type === undefined) return null;
  const fmtp = lines.find((line) => line.startsWith(`a=fmtp:${type} `));
  return `opus ${type}${fmtp === undefined ? ' · no fmtp' : ` · ${fmtp.slice(`a=fmtp:${type} `.length)}`}`;
}
